import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  Car,
  DollarSign,
  HardHat,
  Package,
  RefreshCw,
  Users,
  Wallet,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from '../../components/ui/GlassCard';
import { GlassChart } from '../../components/ui/GlassChart';
import { GlassButton } from '../../components/ui/GlassButton';
import { FadeIn, StaggerContainer, StaggerItem } from '../../components/ui/Animations';
import { api } from '../../hooks/useApi';

const cardIcons = {
  saldo_caixa: Wallet,
  resultado_mes: DollarSign,
  unidades_estoque: Building2,
  alertas: AlertTriangle,
  funcionarios: Users,
  veiculos: Car,
  equipamentos: Package,
  leads: HardHat,
};

const moduleIcons = {
  financeiro: DollarSign,
  construtora: Building2,
  rh: Users,
  frota: Car,
  estoque: Package,
  imobiliaria: HardHat,
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const formatNumber = (value) => Number(value || 0).toLocaleString('pt-BR');

const formatCardValue = (card) => (card.tipo === 'moeda' ? formatMoney(card.valor) : formatNumber(card.valor));

const levelClasses = {
  critico: 'bg-red-500/15 text-red-200 border-red-400/30',
  alto: 'bg-amber-500/15 text-amber-100 border-amber-400/30',
  alta: 'bg-amber-500/15 text-amber-100 border-amber-400/30',
  medio: 'bg-sky-500/15 text-sky-100 border-sky-400/30',
  media: 'bg-sky-500/15 text-sky-100 border-sky-400/30',
  baixa: 'bg-white/[0.04] text-white/60 border-white/10',
  erro: 'bg-red-500/15 text-red-200 border-red-400/30',
};

const metricLines = (moduleId, metrics = {}) => {
  const map = {
    financeiro: [
      ['Pagar vencido', formatMoney(metrics.pagar_vencido?.valor)],
      ['Receber vencido', formatMoney(metrics.receber_vencido?.valor)],
      ['Contas', formatNumber(metrics.contas_bancarias)],
    ],
    construtora: [
      ['Obras em andamento', formatNumber(metrics.obras_andamento)],
      ['Unidades disponiveis', formatNumber(metrics.unidades?.disponiveis)],
      ['Terrenos em avaliacao', formatNumber(metrics.terrenos_avaliacao)],
    ],
    rh: [
      ['Ativos', formatNumber(metrics.funcionarios_ativos)],
      ['Ferias vencidas', formatNumber(metrics.ferias_vencidas)],
      ['Empreiteira docs', formatNumber(metrics.empreiteiras_documentos_revisao)],
    ],
    frota: [
      ['Ativos', formatNumber(metrics.veiculos_ativos)],
      ['Multas abertas', formatNumber(metrics.multas_abertas?.quantidade)],
      ['Usos no mes', formatNumber(metrics.usos_mes)],
    ],
    estoque: [
      ['Materiais baixos', formatNumber(metrics.materiais_baixo_estoque)],
      ['Equipamentos', formatNumber(metrics.equipamentos_total)],
      ['Manutencoes vencidas', formatNumber(metrics.equipamentos_manutencao_vencida)],
    ],
    imobiliaria: [
      ['Leads locais', formatNumber(metrics.leads_total)],
      ['Anuncios venda', formatNumber(metrics.anuncios_venda_ativos)],
      ['Propostas pendentes', formatNumber(metrics.propostas_pendentes)],
    ],
  };
  return map[moduleId] || [];
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDashboard = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/erp/painel-executivo');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Falha ao carregar painel executivo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const cards = data?.cards || [];
  const alertas = data?.alertas || [];
  const modulos = data?.modulos_resumo || [];
  const serie = data?.serie_financeira || [];
  const kpis = data?.kpis || [];
  const implantacao = data?.implantacao_geral;
  const proximasAcoes = data?.proximas_acoes || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Painel Executivo</h1>
          <p className="text-white/50">
            Visao geral operacional do ERP em {data?.data_referencia || 'tempo real'}
          </p>
        </div>
        <GlassButton icon={RefreshCw} onClick={loadDashboard} loading={loading}>
          Atualizar
        </GlassButton>
      </div>

      {error && (
        <GlassCard className="p-5 border-red-400/30 bg-red-500/10">
          <div className="flex items-start gap-3 text-red-100">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">Nao foi possivel carregar o painel</p>
              <p className="text-sm text-red-100/70">{error}</p>
            </div>
          </div>
        </GlassCard>
      )}

      <StaggerContainer className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = cardIcons[card.id] || DollarSign;
          return (
            <StaggerItem key={card.id}>
              <GlassCard
                className={`p-5 ${card.rota ? 'cursor-pointer transition-colors hover:bg-white/[0.06]' : ''}`}
                onClick={() => card.rota && navigate(card.rota)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-white/50">{card.titulo}</p>
                    <p className="mt-2 break-words text-2xl font-bold text-white/90">
                      {formatCardValue(card)}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-white/40">{card.descricao}</p>
                  </div>
                  <div className="rounded-lg bg-white/10 p-3">
                    <Icon className="h-5 w-5 text-accent-primary" />
                  </div>
                </div>
              </GlassCard>
            </StaggerItem>
          );
        })}
      </StaggerContainer>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <FadeIn className="xl:col-span-2">
          <GlassCard>
            <div className="border-b border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white/90">Financeiro dos ultimos 6 meses</h3>
              <p className="text-sm text-white/50">Receitas e despesas por vencimento</p>
            </div>
            <div className="p-6">
              {serie.length ? (
                <GlassChart
                  type="area"
                  data={serie}
                  dataKeys={[
                    { key: 'receitas', name: 'Receitas' },
                    { key: 'despesas', name: 'Despesas' },
                  ]}
                  height={300}
                />
              ) : (
                <div className="flex h-[300px] items-center justify-center text-white/40">
                  Sem movimentacoes suficientes para o grafico
                </div>
              )}
            </div>
          </GlassCard>
        </FadeIn>

        <FadeIn>
          <GlassCard>
            <div className="border-b border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white/90">Alertas prioritarios</h3>
              <p className="text-sm text-white/50">{alertas.length} pendencias ativas</p>
            </div>
            <div className="max-h-[385px] space-y-3 overflow-y-auto p-6">
              {alertas.length === 0 && (
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-sm text-white/50">
                  Nenhum alerta critico no momento.
                </div>
              )}
              {alertas.slice(0, 8).map((alerta, index) => (
                <button
                  key={`${alerta.titulo}-${index}`}
                  type="button"
                  onClick={() => alerta.rota && navigate(alerta.rota)}
                  className="w-full rounded-lg border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:bg-white/[0.07]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${levelClasses[alerta.nivel] || levelClasses.medio}`}>
                        {alerta.nivel}
                      </span>
                      <p className="mt-3 text-sm font-semibold text-white/90">{alerta.titulo}</p>
                      <p className="mt-1 text-xs leading-5 text-white/50">{alerta.descricao}</p>
                      <p className="mt-2 text-xs text-accent-primary">{alerta.acao}</p>
                    </div>
                    {alerta.rota && <ArrowRight className="mt-1 h-4 w-4 flex-shrink-0 text-white/40" />}
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>
        </FadeIn>
      </div>

      <FadeIn>
        <GlassCard>
          <div className="flex flex-col gap-3 border-b border-white/10 p-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white/90">Plano de ação de hoje</h3>
              <p className="text-sm text-white/50">Prioridades combinando alertas reais e implantação mínima dos módulos</p>
            </div>
            <GlassButton icon={ArrowRight} variant="secondary" onClick={() => navigate('/predictive')}>
              Abrir IA Preditiva
            </GlassButton>
          </div>
          <div className="divide-y divide-white/10">
            {!proximasAcoes.length && (
              <div className="p-6 text-sm text-white/50">
                Nenhuma ação operacional crítica agora. O próximo ganho vem de alimentar dados reais nos módulos.
              </div>
            )}
            {proximasAcoes.slice(0, 6).map((acao, index) => (
              <button
                key={`${acao.origem}-${acao.titulo}-${index}`}
                type="button"
                onClick={() => acao.rota && navigate(acao.rota)}
                className="grid w-full grid-cols-1 gap-4 p-5 text-left transition-colors hover:bg-white/[0.05] lg:grid-cols-[auto_1fr_auto]"
              >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-sm font-semibold text-white/70">
                  {index + 1}
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${levelClasses[acao.prioridade] || levelClasses.medio}`}>
                      {acao.prioridade}
                    </span>
                    <span className="text-xs uppercase text-white/35">{acao.origem}</span>
                    {acao.modulo && <span className="text-xs text-white/35">{acao.modulo}</span>}
                  </div>
                  <p className="mt-3 font-semibold text-white/90">{acao.titulo}</p>
                  <p className="mt-1 text-sm leading-5 text-white/50">{acao.motivo}</p>
                  <p className="mt-2 text-sm text-accent-primary">{acao.acao}</p>
                </div>
                {acao.rota && <ArrowRight className="mt-1 h-5 w-5 text-white/35" />}
              </button>
            ))}
          </div>
        </GlassCard>
      </FadeIn>

      <FadeIn>
        <GlassCard>
          <div className="border-b border-white/10 p-6">
            <h3 className="text-lg font-semibold text-white/90">KPIs operacionais</h3>
            <p className="text-sm text-white/50">Metas e riscos que ajudam a orientar a rotina do dono</p>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-5">
            {kpis.map((kpi) => {
              const value = kpi.tipo === 'moeda' ? formatMoney(kpi.valor) : formatNumber(kpi.valor);
              const hasTarget = kpi.meta !== null && kpi.meta !== undefined;
              const progress = hasTarget && Number(kpi.meta) > 0
                ? Math.min(100, Math.round((Number(kpi.valor || 0) / Number(kpi.meta)) * 100))
                : null;
              return (
                <button
                  key={kpi.id}
                  type="button"
                  onClick={() => kpi.rota && navigate(kpi.rota)}
                  className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:bg-white/[0.07]"
                >
                  <p className="text-xs text-white/45">{kpi.titulo}</p>
                  <p className="mt-2 text-xl font-semibold text-white/90">{value}</p>
                  {hasTarget && (
                    <p className="mt-1 text-xs text-white/45">
                      Meta: {kpi.tipo === 'moeda' ? formatMoney(kpi.meta) : formatNumber(kpi.meta)}
                    </p>
                  )}
                  {progress !== null && (
                    <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                      <div className="h-full rounded-full bg-accent-primary" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                  <p className="mt-3 text-xs leading-5 text-white/50">{kpi.descricao}</p>
                </button>
              );
            })}
          </div>
        </GlassCard>
      </FadeIn>

      <FadeIn>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {modulos.map((modulo) => {
            const Icon = moduleIcons[modulo.id] || Package;
            const statusOk = modulo.status === 'ok';
            return (
              <GlassCard key={modulo.id} className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-white/10 p-3">
                      <Icon className="h-5 w-5 text-white/80" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white/90">{modulo.nome}</h3>
                      <p className={statusOk ? 'text-xs text-green-300' : 'text-xs text-amber-300'}>
                        {statusOk ? 'Operacao sem alerta principal' : 'Precisa de atencao'}
                      </p>
                    </div>
                  </div>
                  <GlassButton
                    variant="ghost"
                    size="icon"
                    icon={ArrowRight}
                    aria-label={`Abrir ${modulo.nome}`}
                    onClick={() => navigate(modulo.rota)}
                  />
                </div>
                <div className="mt-5 grid grid-cols-1 gap-3">
                  {metricLines(modulo.id, modulo.metricas).map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2">
                      <span className="text-xs text-white/45">{label}</span>
                      <span className="text-sm font-semibold text-white/85">{value}</span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            );
          })}
        </div>
      </FadeIn>

      {implantacao && (
        <FadeIn>
          <GlassCard>
            <div className="border-b border-white/10 p-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-white/90">Implantação geral do ERP</h3>
                  <p className="text-sm text-white/50">Mapa do que ainda falta estruturalmente por módulo, sem contar alimentação operacional diária</p>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-2xl font-semibold text-white/90">{implantacao.progresso_percentual}%</p>
                  <p className="text-xs text-white/45">{implantacao.concluidos}/{implantacao.total} itens concluídos</p>
                </div>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-accent-primary" style={{ width: `${implantacao.progresso_percentual}%` }} />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 p-6 lg:grid-cols-2">
              {(implantacao.itens || []).map((item) => (
                <button
                  key={item.chave}
                  type="button"
                  onClick={() => item.rota && navigate(item.rota)}
                  className="rounded-lg border border-white/10 bg-white/[0.03] p-4 text-left transition-colors hover:bg-white/[0.07]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-white/35">{item.modulo}</p>
                      <p className="mt-1 text-sm font-semibold text-white/90">{item.titulo}</p>
                      <p className="mt-1 text-xs leading-5 text-white/50">{item.descricao}</p>
                    </div>
                    <span className={item.concluido ? 'rounded-full bg-green-500/15 px-2 py-1 text-xs text-green-300' : 'rounded-full bg-amber-500/15 px-2 py-1 text-xs text-amber-200'}>
                      {item.atual}/{item.minimo}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </GlassCard>
        </FadeIn>
      )}
    </div>
  );
};

export default Dashboard;
